<template>
  <Layout>
    <section>
      <header>
        <h2>{{ 'Personal Details' | t }}</h2>
      </header>
      <main>
        <SloLoader :loading="loading" size="xl">
          <form @submit.prevent="handleNameUpdate" class="personal-details personal-details--full-name">
            <div class="p-md sm:p-lg">
              <div class="flex flex-col items-start sm:items-end sm:flex-row sm:justify-between">
                <h3 class="text-xl fw-strong">{{ 'User information' | t }}</h3>
                <p class="m-0">{{ 'Account #' | t }}{{ user.accountNumber }}</p>
              </div>
              <div class="personal-details--form flex flex-col items-stretch md:flex-row">
                <SloField>
                  <SloLabel>{{ 'First Name' | t }}</SloLabel>
                  <SloInput
                    v-model="firstName"
                    :disabled="nameUpdateLoading"
                    name="firstName"
                    type="text"
                    required
                  />
                </SloField>
                <SloField>
                  <SloLabel>{{ 'Last Name' | t }}</SloLabel>
                  <SloInput
                    v-model="lastName"
                    :disabled="nameUpdateLoading"
                    name="lastName"
                    type="text"
                    required
                  />
                </SloField>
              </div>
            </div>
            <div class="personal-details--footer flex justify-end px-md py-md bg-gray-100 sm:px-lg">
              <SloButton
                :disabled="nameUpdateLoading || !firstName || !lastName"
                type="submit"
                fill="solid"
              >
                <SloLoader :loading="nameUpdateLoading" size="md" color="white">
                  {{ 'Save' | t }}
                </SloLoader>
              </SloButton>
            </div>
          </form>
          <form @submit.prevent="handleEmailUpdate" class="personal-details personal-details--email mt-lg">
            <div class="p-md sm:p-lg">
              <div class="flex flex-col items-start">
                <h3 class="text-xl fw-strong">{{ 'User email' | t }}</h3>
                <p class="mt-sm mb-0">
                  {{ 'Updating your email address will sign you out and you will be redirected to the sign in page.' | t }}
                </p>
              </div>
              <div class="personal-details--form flex flex-col items-stretch md:flex-row">
                <SloField>
                  <SloLabel>{{ 'Current email' | t }}</SloLabel>
                  <SloInput
                    v-model="oldEmail"
                    :disabled="emailUpdateLoading"
                    name="oldEmail"
                    type="email"
                    disabled
                    required
                  />
                </SloField>
                <SloField>
                  <SloLabel :error="newEmailError">{{ 'New email address' | t }}</SloLabel>
                  <SloInput
                    v-model="newEmail"
                    :disabled="emailUpdateLoading"
                    :error="newEmailError && 'New email cannot be the same as the current email' | t"
                    name="newEmail"
                    type="email"
                    required
                  />
                </SloField>
              </div>
              <div class="personal-details--form flex flex-col items-stretch md:flex-row">
                <SloField>
                  <SloLabel :error="confirmEmailError">{{ 'Confirm new email address' | t }}</SloLabel>
                  <SloInput
                    v-model="confirmEmail"
                    :disabled="emailUpdateLoading"
                    :error="confirmEmailError && 'New email does not match confirm' | t"
                    name="confirmEmail"
                    type="email"
                    required
                  />
                </SloField>
                <SloField>
                  <SloLabel>{{ 'Current password' | t }}</SloLabel>
                  <SloInput
                    v-model="emailChangePassword"
                    name="emailChangePassword"
                    type="password"
                    id="emailChangePassword"
                    required
                  >
                    <template v-slot:end>
                      <SloIcon
                        :name="emailChangePasswordVisible ? 'EyeSlashSolid' : 'EyeSolid'"
                         @click="togglePasswordVisibility('emailChangePassword')"
                      />
                    </template>
                  </SloInput>
                </SloField>
              </div>
            </div>
            <div class="personal-details--footer flex justify-end px-md py-md bg-gray-100 sm:px-lg">
              <SloButton
                :disabled="emailUpdateLoading || !oldEmail || !newEmail || !confirmEmail || !emailChangePassword"
                type="submit"
                fill="solid"
              >
                <SloLoader :loading="emailUpdateLoading" size="md" color="white">
                  {{ 'Save' | t }}
                </SloLoader>
              </SloButton>
            </div>
          </form>
          <form @submit.prevent="handlePasswordUpdate" class="personal-details personal-details--password mt-lg">
            <div class="p-md sm:p-lg">
              <div class="flex flex-col items-start">
                <h3 class="text-xl fw-strong">{{ 'Update password' | t }}</h3>
                <p class="mt-sm mb-0">{{ 'New passwords must contain one uppercase character, one lowercase character, one number, and be 8 characters minimum.' | t }}</p>
              </div>
              <div class="personal-details--form flex flex-col items-stretch md:flex-row">
                <SloField>
                  <SloLabel>{{ 'Old password' | t }}</SloLabel>
                  <SloInput
                    v-model="oldPassword"
                    :disabled="passwordUpdateLoading"
                    name="oldPassword"
                    type="password"
                    id="currentPassword"
                    required
                  >
                    <template v-slot:end>
                      <SloIcon
                        :name="oldPasswordVisible ? 'EyeSlashSolid' : 'EyeSolid'"
                        @click="togglePasswordVisibility('currentPassword')"
                      />
                    </template>
                  </SloInput>
                </SloField>
                <SloField>
                  <SloLabel>{{ 'New password' | t }}</SloLabel>
                  <SloInput
                    v-model="newPassword"
                    :disabled="passwordUpdateLoading"
                    name="newPassword"
                    type="password"
                    id="newPassword"
                    required
                  >
                    <template v-slot:end>
                      <SloIcon
                        :name="newPasswordVisible ? 'EyeSlashSolid' : 'EyeSolid'"
                        @click="togglePasswordVisibility('newPassword')"
                      />
                    </template>
                  </SloInput>
                </SloField>
                <SloField>
                  <SloLabel :error="confirmPasswordError">{{ 'Confirm new password' | t }}</SloLabel>
                  <SloInput
                    v-model="confirmPassword"
                    :disabled="passwordUpdateLoading"
                    :error="confirmPasswordError && 'New password does not match confirm' | t"
                    name="confirmPassword"
                    type="password"
                    id="confirmPassword"
                    required
                  >
                    <template v-slot:end>
                      <SloIcon
                        :name="confirmPasswordVisible ? 'EyeSlashSolid' : 'EyeSolid'"
                        @click="togglePasswordVisibility('confirmPassword')"
                      />
                    </template>
                  </SloInput>
                </SloField>
              </div>
            </div>
            <div class="personal-details--footer flex justify-end px-md py-md bg-gray-100 sm:px-lg">
              <SloButton
                :disabled="passwordUpdateLoading || !oldPassword || !newPassword || !confirmPassword"
                type="submit"
                fill="solid"
              >
                <SloLoader :loading="passwordUpdateLoading" size="md" color="white">
                  {{ 'Save' | t }}
                </SloLoader>
              </SloButton>
            </div>
          </form>
        </SloLoader>
      </main>
    </section>
  </Layout>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import t from '../../main/i18n';
import {handleApiErrors} from '../../main/utils';
import {marketplaceApi} from '../apis/marketplace';
import Layout from '../components/Layout.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';

export default {
  components: {
    Layout,
    SloField,
    SloInput,
    SloLabel,
    SloButton,
    SloIcon,
    SloLoader
  },
  filters: {
    t
  },
  data() {
    return {
      loading: true,
      nameUpdateLoading: false,
      emailUpdateLoading: false,
      passwordUpdateLoading: false,
      newEmailError: false,
      confirmEmailError: false,
      confirmPasswordError: false,
      firstName: '',
      lastName: '',
      oldEmail: '',
      newEmail: '',
      confirmEmail: '',
      emailChangePassword: '',
      emailChangePasswordVisible: false,
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      oldPasswordVisible: false,
      newPasswordVisible: false,
      confirmPasswordVisible: false
    };
  },
  computed: mapGetters('auth', ['user']),
  async created() {
    this.loading = true;
    await this.getCurrentUser()
      .then(() => {
        this.loading = false;
        this.firstName = this.user.firstName;
        this.lastName = this.user.lastName;
        this.oldEmail = this.user.email;
      })
      .catch(e => {
        this.loading = false;
        handleApiErrors(e, this, 'Unable to get current user details');
      });
  },
  methods: {
    ...mapActions('auth', ['getCurrentUser', 'logout']),
    togglePasswordVisibility(targetID) {
      const target = document.getElementById(targetID);

      if (target.type === 'password') {
        target.type = 'text';
      } else {
        target.type = 'password';
      }

      if (targetID === 'currentPassword') {
        this.oldPasswordVisible = !this.oldPasswordVisible;
      } else if (targetID === 'newPassword') {
        this.newPasswordVisible = !this.newPasswordVisible;
      } else if (targetID === 'confirmPassword') {
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
      } else if (targetID === 'emailChangePassword') {
        this.emailChangePasswordVisible = !this.emailChangePasswordVisible;
      }
    },
    async handleNameUpdate() {
      this.nameUpdateLoading = true;
      await marketplaceApi.updateUserName({
        firstName: this.firstName,
        lastName: this.lastName
      })
        .then(async() => {
          await this.getCurrentUser();
          this.nameUpdateLoading = false;
          this.$snotify.success(t('Your name has been updated'));
        })
        .catch(e => {
          this.nameUpdateLoading = false;
          handleApiErrors(e, this, 'Unable to update your name');
        });
    },
    async handleEmailUpdate() {
      if (this.newEmail === this.oldEmail) {
        this.newEmailError = true;
        return;
      }
      this.newEmailError = false;
      if (this.newEmail !== this.confirmEmail) {
        this.confirmEmailError = true;
        return;
      }
      this.confirmEmailError = false;

      this.emailUpdateLoading = true;
      await marketplaceApi.updateUserEmail(this.newEmail, this.emailChangePassword)
        .then(() => {
          this.logout('user_email_change');
          this.emailUpdateLoading = false;
        })
        .catch(e => {
          this.emailUpdateLoading = false;
          handleApiErrors(e, this, 'Unable to update your email');
        });
    },
    async handlePasswordUpdate() {
      if (this.newPassword !== this.confirmPassword) {
        this.confirmPasswordError = true;
        return;
      }
      this.confirmPasswordError = false;

      this.passwordUpdateLoading = true;
      await marketplaceApi.updateUserPassword(this.oldPassword, this.newPassword)
        .then(async() => {
          await this.getCurrentUser();
          this.passwordUpdateLoading = false;
          this.oldPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          this.$snotify.success(t('Your password has been updated'));
        })
        .catch(e => {
          this.passwordUpdateLoading = false;
          handleApiErrors(e, this, 'Unable to update your password');
        });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/system';

.personal-details {
  border: 1px solid $color-gray-400;
  border-radius: 1.2rem;

  &--form {
    gap: 1.6rem;
    width: 100%;
    margin-block-start: 2.4rem;

    @media (min-width: 767px) {
      gap: 2.4rem;
    }

    .slo-field {
      flex: 1;
    }

    .slo-input {
      position: relative;
    }

    .slo-icon {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      color: $color-gray-400;
      cursor: pointer;
      transition: color 0.15s ease-in;

      &:hover {
        color: $color-gray-700;
      }
    }
  }

  &--footer {
    border-top: 1px solid $color-gray-400;
    border-radius: 0 0 1.2rem 1.2rem;
  }
}
</style>
